import { MatchBrowseQuery } from '@/services/api/matchesV2';
import { MatchesURLState } from './types';
import { sanitizeReturnPath } from '@/routes/returnPath';

export const defaultMatchesURLState: MatchesURLState = {
  q: '', source: '', status: '', venue: '', competition: '', season: '', from: '', to: '',
  sort: 'date', order: 'desc', page: 1, pageSize: 20,
};

const datePattern = /^\d{4}-\d{2}-\d{2}$/;
const validDate = (value: string): boolean => {
  if (!datePattern.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

export const parseMatchesURLState = (params: URLSearchParams): MatchesURLState => {
  const source = params.get('source');
  const status = params.get('status');
  const venue = params.get('venue');
  const sort = params.get('sort');
  const order = params.get('order');
  const page = Number(params.get('page'));
  const pageSize = Number(params.get('page_size'));
  let from = params.get('from') || '';
  let to = params.get('to') || '';
  from = validDate(from) ? from : '';
  to = validDate(to) ? to : '';
  if (from && to && from > to) { from = ''; to = ''; }
  return {
    q: (params.get('q') || '').trim(),
    source: source === 'manual' || source === 'wyscout' ? source : '',
    status: ['upcoming', 'live', 'played', 'postponed', 'cancelled'].includes(status || '') ? status as MatchesURLState['status'] : '',
    venue: venue === 'home' || venue === 'away' || venue === 'neutral' ? venue : '',
    competition: (params.get('competition') || '').trim(),
    season: (params.get('season') || '').trim(),
    from, to,
    sort: sort === 'competition' || sort === 'status' ? sort : 'date',
    order: order === 'asc' ? 'asc' : 'desc',
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: pageSize === 50 || pageSize === 100 ? pageSize : 20,
  };
};

export const serializeMatchesURLState = (state: MatchesURLState): URLSearchParams => {
  const params = new URLSearchParams();
  const add = (key: string, value: string | number) => { if (value !== '') params.set(key, String(value)); };
  add('q', state.q.trim()); add('source', state.source); add('status', state.status); add('venue', state.venue);
  add('competition', state.competition); add('season', state.season); add('from', state.from); add('to', state.to);
  if (state.sort !== 'date') add('sort', state.sort); if (state.order !== 'desc') add('order', state.order);
  if (state.page !== 1) add('page', state.page); if (state.pageSize !== 20) add('page_size', state.pageSize);
  return params;
};

export const matchesStateToQuery = (state: MatchesURLState): MatchBrowseQuery => ({
  ...(state.q ? { q: state.q } : {}), ...(state.source ? { source: state.source } : {}),
  ...(state.status ? { status: state.status } : {}), ...(state.venue ? { venue: state.venue } : {}),
  ...(state.competition ? { competition: state.competition } : {}), ...(state.season ? { season: state.season } : {}),
  ...(state.from ? { from: state.from } : {}), ...(state.to ? { to: state.to } : {}),
  sort: state.sort, order: state.order, page: state.page, pageSize: state.pageSize,
});

export const safeMatchesReturnTo = (value: string | null): string => sanitizeReturnPath(value, '/matches');

export const parseMatchTab = <T extends string>(params: URLSearchParams, allowed: readonly T[], fallback: T): T => {
  const tab = params.get('tab');
  return allowed.includes(tab as T) ? tab as T : fallback;
};

export const setMatchTab = (params: URLSearchParams, tab: string, fallback: string): URLSearchParams => {
  const next = new URLSearchParams(params);
  if (tab === fallback) next.delete('tab'); else next.set('tab', tab);
  return next;
};
